import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Shield, CheckCircle, Users, Award, Clock, FileText, 
  Search, TrendingUp, Lock, Mail, Phone, MapPin, 
  ChevronRight, Menu, X, ArrowRight, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ContactPopup } from '../components/ContactPopup';
import { PricingCard } from '../components/PricingCard';
import { contactInfo, trustSignals, industries } from '../mock';

const Home = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentPriceIndex, setCurrentPriceIndex] = useState(0);

  const openPopup = (message = '') => {
    setPopupMessage(message);
    setIsPopupOpen(true);
  };

  const pricingPlans = [
    {
      name: 'Essentials',
      price: 45,
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured'
      ]
    },
    {
      name: 'Enhanced',
      price: 149,
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter'
      ]
    },
    {
      name: 'Deluxe',
      price: 299,
      description: 'Most Popular Choice',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager'
      ]
    },
    {
      name: 'Platinum',
      price: 349,
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager',
        'High Approval Rate',
        'Comprehensive Search Report',
        'High Priority Service',
        'Cease & Desist Letter'
      ]
    }
  ];

  const navigationLinks = [
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Process', href: '#process' },
    { name: 'Pricing', href: '#pricing' },
    { name: 'FAQs', href: '#faqs' },
    { name: 'Contact', href: '#contact' }
  ];

  const services = [
    {
      icon: FileText,
      title: 'Trademark Filing',
      description: 'Complete trademark application preparation and filing with the USPTO, ensuring all documentation meets federal requirements.'
    },
    {
      icon: Search,
      title: 'Comprehensive Search',
      description: 'Thorough trademark search to identify potential conflicts and increase your approval chances before filing.'
    },
    {
      icon: Shield,
      title: 'Brand Protection',
      description: 'Continuous monitoring and protection of your trademark rights against infringement and unauthorized use.'
    },
    {
      icon: FileText,
      title: 'Office Action Response',
      description: 'Expert handling of USPTO office actions and objections to keep your application moving forward.'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Consultation & Comprehensive Search',
      description: 'Begin with a free consultation where our trademark experts evaluate your brand identity and business needs. We then conduct an extensive trademark search across federal and state databases, including common law uses, to identify potential conflicts. Our detailed analysis includes phonetic and visual similarity assessments, ensuring your mark has the highest chance of approval. You\'ll receive a comprehensive report outlining our findings and recommendations, giving you complete confidence before filing.'
    },
    {
      step: '02',
      title: 'Application Preparation & USPTO Filing',
      description: 'Our experienced team meticulously prepares your trademark application, ensuring every detail meets USPTO requirements. We draft precise descriptions of goods and services, select appropriate international classes, and compile all necessary specimens and supporting documentation. After your review and approval, we electronically file your application with the USPTO, securing your filing date and providing you with immediate confirmation and tracking information for your application.'
    },
    {
      step: '03',
      title: 'Monitoring, Response & Registration',
      description: 'Throughout the 8-12 month examination process, we actively monitor your application status and handle all USPTO correspondence. If the examining attorney issues an office action, we prepare expert legal responses to overcome objections. We keep you informed at every stage with regular status updates. Upon approval, your mark is published for opposition, and after the waiting period, you receive your official trademark registration certificate with full federal protection and the exclusive right to use the ® symbol.'
    }
  ];

  const benefits = [
    {
      icon: Lock,
      title: 'Nationwide Exclusive Legal Protection',
      description: 'Federal trademark registration grants you exclusive nationwide rights to use your brand name, logo, or slogan in connection with your goods or services. This legal protection creates a presumption of ownership and validity in court, making it significantly easier to enforce your rights against infringers. You gain the power to prevent competitors from using confusingly similar marks anywhere in the United States, regardless of whether they were using the mark before your registration in certain geographic areas. This protection extends to online commerce, e-commerce platforms, and digital marketplaces, giving you comprehensive control over your brand identity across all channels and territories. The registration serves as a powerful legal barrier against unauthorized use and trademark squatting.'
    },
    {
      icon: TrendingUp,
      title: 'Enhanced Business Value & Market Credibility',
      description: 'A registered trademark significantly increases your company\'s valuation and attractiveness to investors, lenders, and potential buyers. It represents tangible intellectual property that appears on your balance sheet as a valuable business asset. The ® symbol instantly communicates professionalism, legitimacy, and permanence to customers, partners, and competitors. Registered trademarks can be licensed or franchised, creating additional revenue streams for your business. When seeking funding or partnerships, a strong trademark portfolio demonstrates your commitment to brand building and market position. Studies show that businesses with registered trademarks command higher prices in acquisitions and enjoy increased customer trust and loyalty, directly impacting your bottom line and long-term success.'
    },
    {
      icon: Shield,
      title: 'Strong Infringement Prevention & Enforcement Rights',
      description: 'Registration provides you with powerful legal tools to stop trademark infringement quickly and effectively. You can issue cease and desist letters backed by federal registration, file complaints with online marketplaces to remove counterfeit goods, pursue legal action in federal court with statutory damages available, and potentially recover attorney fees in successful infringement cases. The registration creates a public record that deters potential infringers and makes it difficult for others to claim they were unaware of your rights. You can also record your trademark with U.S. Customs and Border Protection to prevent the importation of counterfeit goods. This proactive protection shields your brand reputation, prevents customer confusion, and stops competitors from diluting your market position or free-riding on your goodwill.'
    },
    {
      icon: Award,
      title: 'Competitive Market Advantage & Growth Opportunities',
      description: 'A registered trademark gives you a sustainable competitive advantage that competitors cannot easily replicate or challenge. It establishes your brand as the senior user in your field, blocking later entrants from adopting similar names or designs. This protection is crucial for business expansion, whether you\'re entering new markets, launching product lines, or pursuing franchise opportunities. Registration facilitates international expansion through treaty-based systems like the Madrid Protocol, allowing you to extend protection to over 120 countries. Your trademark becomes a marketing asset that differentiates your products in crowded markets, builds customer recognition, and supports premium pricing strategies. Over time, strong trademarks develop goodwill and brand equity that can become more valuable than physical assets, creating lasting competitive moats around your business.'
    }
  ];

  const whyChooseUs = [
    {
      icon: Users,
      title: 'Seasoned Trademark Experts',
      description: 'Our team brings over 15 years of specialized trademark law experience, having successfully navigated thousands of USPTO applications across all industries. We stay current with evolving trademark regulations, examination guidelines, and case law to provide you with cutting-edge strategies. Our specialists understand the nuances of trademark prosecution and can handle complex issues including likelihood of confusion analyses, distinctiveness arguments, and specimen challenges.'
    },
    {
      icon: CheckCircle,
      title: '98% Registration Success Rate',
      description: 'We maintain an exceptional 98% approval rate for trademark applications, far exceeding industry averages. This success comes from our meticulous preparation, comprehensive pre-filing searches, strategic classification selection, and expert office action responses. Our proven methodologies minimize rejections and accelerate the registration process, saving you time and money while protecting your brand investment with confidence.'
    },
    {
      icon: Clock,
      title: 'Efficient & Transparent Process',
      description: 'We pride ourselves on rapid turnaround times without sacrificing quality. Most applications are prepared and filed within 2-3 business days of receiving complete information. You\'ll receive real-time updates via email and our client portal, with direct access to your case manager. We proactively monitor deadlines, respond promptly to USPTO communications, and keep you informed at every milestone throughout the entire registration journey.'
    },
    {
      icon: Shield,
      title: 'Comprehensive Lifetime Support',
      description: 'Your relationship with us doesn\'t end at registration. We provide ongoing support including renewal reminders, maintenance deadline tracking, expansion consulting, and enforcement guidance. Each client receives a dedicated case manager who becomes an expert in your brand, providing personalized service and strategic advice. We\'re here to protect and grow your trademark portfolio for the long term, serving as your trusted intellectual property partner.'
    }
  ];

  const faqs = [
    {
      question: 'What is a trademark and why do I need one?',
      answer: 'A trademark is a distinctive word, phrase, symbol, design, or combination that identifies and distinguishes your goods or services from those of competitors. It can be your business name, logo, slogan, product name, or even unique packaging. Federal trademark registration is essential because it provides nationwide exclusive rights, legal presumption of ownership, and the ability to sue infringers in federal court. Without registration, you only have common law rights limited to your geographic area of use. A registered trademark adds tangible value to your business, prevents competitors from using similar marks, protects your brand reputation, enables licensing opportunities, and is crucial for e-commerce and online marketplaces. It\'s one of the most important investments you can make to protect your business identity and build long-term brand equity.'
    },
    {
      question: 'How long does the trademark registration process take?',
      answer: 'The complete trademark registration process typically takes 8-12 months from filing to final registration, though timelines can vary. Here\'s the breakdown: After filing, you receive a filing receipt within 1-2 days. The USPTO assigns an examining attorney within 3-4 months who reviews your application for compliance and potential conflicts. If no issues arise, your mark is approved and published in the Official Gazette for a 30-day opposition period. If no oppositions are filed, you receive your registration certificate 2-3 months later. If the examiner issues an office action, you have six months to respond, which can extend the timeline. However, you can begin using the ™ symbol immediately after filing to indicate your pending trademark rights. Throughout this process, we monitor your application and handle all USPTO correspondence to ensure smooth progress toward registration.'
    },
    {
      question: 'What is the difference between ™ and ® symbols?',
      answer: 'The ™ and ® symbols have important legal distinctions. The ™ symbol (trademark) can be used with any mark for which you claim rights, even without filing or registration. It simply indicates that you consider the term or logo to be your trademark. You can begin using ™ immediately after filing your application or even before filing if you\'re using the mark in commerce. The ® symbol (registered trademark), however, can ONLY be used after the USPTO officially registers your trademark. Using ® before registration is illegal and can result in denial of your application, loss of rights, and potential fraud penalties. The ® symbol provides notice of your federal registration and strengthens your legal position against infringers. It tells competitors and the public that your mark is officially registered with the U.S. government, carries presumptions of validity and nationwide priority, and you have the full force of federal law behind your trademark rights.'
    },
    {
      question: 'Can I trademark a business name?',
      answer: 'Yes, you can trademark your business name, but important distinctions apply. Simply registering your business name with your state as an LLC or corporation does NOT provide trademark protection - these are completely different systems. State business registration only ensures no other entity in your state has that exact corporate name; it doesn\'t prevent others from using similar names for similar products or services. To protect your business name as a trademark, you must use it in commerce to identify your goods or services AND register it with the USPTO. The name must be distinctive (not merely descriptive) and not confusingly similar to existing registered trademarks. For example, "Smith Consulting" is weak and hard to protect, while "Zenith Consulting" is stronger. Your business name receives the strongest protection when used as a brand identifier on your products, services, marketing materials, and website. Federal trademark registration gives you nationwide exclusive rights to use that business name in your industry, prevents competitors from adopting similar names, and protects your brand reputation across all markets and platforms.'
    },
    {
      question: 'What happens if my trademark application is rejected?',
      answer: 'If the USPTO examining attorney identifies issues with your application, they will issue an "office action" - an official letter outlining objections or required clarifications. Common reasons include likelihood of confusion with existing marks, descriptiveness or genericness of your mark, insufficient specimens, or incorrect classification. You then have six months to file a formal response addressing each point raised. This is where our expertise becomes invaluable. Our team analyzes the office action, researches applicable law and precedent, and crafts persuasive legal arguments to overcome objections. We may submit amended descriptions, new specimens, disclaimers, or detailed legal briefs citing case law. Many office actions can be successfully overcome with proper responses. If the examining attorney maintains the refusal, you can request reconsideration, appeal to the Trademark Trial and Appeal Board, or in some cases modify your mark. We have extensive experience handling all types of office actions and achieving approval despite initial rejections. Our goal is to exhaust all available options to secure your trademark registration. It\'s important to respond timely and thoroughly, which is why having experienced professionals manage this process significantly increases your success rate.'
    },
    {
      question: 'Do you offer trademark monitoring services?',
      answer: 'Yes, ongoing trademark monitoring is included in our Deluxe and Platinum packages, and available as an add-on service for other plans. Our comprehensive monitoring service continuously watches for new trademark applications filed with the USPTO that may conflict with your registered mark, tracking applications in related classes or similar industries. We monitor domain name registrations that incorporate your trademark, scan major e-commerce platforms for unauthorized sellers using your brand, and track social media and online marketplaces for potential infringers. You receive quarterly detailed reports highlighting any potential threats, along with our professional assessment of the risk level and recommended actions. If we identify clear infringement, we can help you take enforcement action through cease and desist letters, opposition proceedings, or other legal remedies. Monitoring is crucial because trademark rights can be lost if you don\'t police them - failure to enforce your trademark against infringers can lead to dilution and potential loss of rights. Our proactive monitoring service helps you maintain strong, enforceable trademark rights and protects the valuable brand equity you\'ve built over time.'
    },
    {
      question: 'What is included in the government filing fee?',
      answer: 'The government filing fee is a separate charge paid directly to the United States Patent and Trademark Office (USPTO) and is NOT included in our service fees. The USPTO currently charges $250-$350 per class of goods or services, depending on the filing basis and application type. The TEAS Plus application ($250/class) requires upfront declaration of all goods/services and selection from USPTO\'s pre-approved descriptions. The TEAS Standard application ($350/class) offers more flexibility in describing your goods and services. These fees are per international class - so if your trademark covers products in 2 classes, you would pay the fee twice. The government fee is non-refundable once your application is submitted, regardless of whether your trademark is ultimately approved. This is separate from our service fees, which cover professional trademark search, application preparation, filing services, attorney review, office action responses, and ongoing support. We transparently disclose all fees upfront so there are no surprises. The government fees are subject to change by the USPTO, and we always confirm current fees before filing your application to ensure accuracy.'
    },
    {
      question: 'Can I file a trademark for multiple products or services?',
      answer: 'Yes, you can absolutely file a single trademark application covering multiple products or services, and in many cases, multiple classes. Trademarks are organized into 45 international classes under the Nice Classification system - Classes 1-34 cover goods (physical products) and Classes 35-45 cover services. Your trademark application must identify every class that applies to your business activities. For example, if you sell clothing (Class 25) and also operate a retail store (Class 35), you would need to cover both classes. Each additional class requires an additional USPTO government filing fee ($250-$350 per class), but you only need one application. It\'s strategically important to file for all relevant classes initially because adding classes later requires a new application. However, you must have a bona fide intent to use your mark in all listed classes - you cannot file defensively for classes you don\'t plan to use. We help you conduct a comprehensive analysis of your current and planned business activities to determine the appropriate classes, ensuring complete protection while avoiding unnecessary expenses. Proper class selection is crucial for maximizing protection and preventing competitors from filing similar marks in classes you overlooked.'
    },
    {
      question: 'How much does trademark registration cost?',
      answer: 'Our trademark registration services range from $45 to $349 depending on the package you select, plus USPTO government filing fees of $250-$350 per class. Our Essentials package ($45) includes case review, filing, basic search, and state e-filing - perfect for straightforward applications. The Enhanced package ($149) adds comprehensive direct-hit search, trademark alerts, satisfaction guarantees, and refusal risk assessment. Our most popular Deluxe package ($299) includes everything in Enhanced plus ongoing trademark monitoring, complete documentation, digital files, and a dedicated case manager who handles every aspect of your application. The premium Platinum package ($349) provides our highest level of service with priority processing, comprehensive search reports, high approval rate guarantees, and cease & desist letter preparation. All packages include professional application preparation, USPTO filing, and basic support. The government filing fees are paid separately to the USPTO per class of goods/services. We provide transparent, all-inclusive pricing with no hidden fees. Payment is required upfront, and we begin work immediately. Most clients find the Deluxe package offers the best value with comprehensive protection and ongoing support that extends well beyond registration.'
    },
    {
      question: 'What if someone else is already using a similar name?',
      answer: 'Discovering a similar mark in use is a common concern and requires careful legal analysis. The key question is whether there\'s a "likelihood of confusion" between the marks - this depends on multiple factors including similarity of the marks, similarity of goods/services, channels of trade, sophistication of consumers, and actual evidence of confusion. If the other party has a federal trademark registration for similar goods/services, your application will likely face rejection. However, if they only have common law rights (using the mark without registration), you may still be able to register if you can show distinctiveness or different markets. If the marks are in completely different industries, both might coexist peacefully. Our comprehensive trademark search identifies these issues before filing, potentially saving you thousands in wasted fees and rebranding costs. If we discover a conflicting mark, we analyze the strength of their rights, assess likelihood of confusion, explore modification options, and provide strategic recommendations. Sometimes slight modifications to your mark, different spelling, different design elements, or targeting different classes can overcome conflicts. In other cases, we might recommend selecting a different mark entirely. We can also investigate whether opposing marks are actually in use or have been abandoned, creating opportunities for your application. The worst scenario is filing blindly, getting rejected after paying fees, and then needing to rebrand your entire business.'
    }
  ];

  // Mobile pricing slider
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setCurrentPriceIndex(0);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextPrice = () => {
    setCurrentPriceIndex((prev) => (prev + 1) % pricingPlans.length);
  };

  const prevPrice = () => {
    setCurrentPriceIndex((prev) => (prev - 1 + pricingPlans.length) % pricingPlans.length);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EFEBE9' }}>
      <Helmet>
        <title>TradeMark Registry - Professional USPTO Trademark Registration Services</title>
        <meta name="description" content="Expert trademark registration services with 98% success rate. Secure your brand with professional USPTO filing, comprehensive searches, and ongoing protection. 1000+ trademarks filed, 15+ years experience." />
        <meta name="keywords" content="trademark registration, USPTO filing, trademark search, brand protection, trademark attorney, register trademark, trademark application, federal trademark, trademark lawyer, intellectual property" />
        <meta property="og:title" content="TradeMark Registry - Professional USPTO Trademark Registration Services" />
        <meta property="og:description" content="Expert trademark registration services with 98% success rate. Protect your brand with professional USPTO filing and comprehensive trademark searches." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://trademark-pro-7.preview.emergentagent.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TradeMark Registry - Professional USPTO Trademark Registration Services" />
        <meta name="twitter:description" content="Expert trademark registration services with 98% success rate. Protect your brand with professional USPTO filing." />
        <link rel="canonical" href="https://trademark-pro-7.preview.emergentagent.com/" />
      </Helmet>
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#3E2723] shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img 
                src="/logo-white.png" 
                alt="TradeMark Registry Logo" 
                className="h-10 w-auto"
              />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              {navigationLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-[#D7CCC8] hover:text-white transition-colors duration-300"
                >
                  {link.name}
                </a>
              ))}
              <Button
                onClick={() => openPopup()}
                className="bg-[#8D6E63] hover:bg-[#6D4C41] text-white"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="md:hidden pb-4">
              {navigationLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block py-2 text-[#D7CCC8] hover:text-white transition-colors duration-300"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <Button
                onClick={() => {
                  openPopup();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full mt-2 bg-[#8D6E63] hover:bg-[#6D4C41] text-white"
              >
                Get Started
              </Button>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="pt-32 pb-20 px-4 relative bg-cover bg-center bg-no-repeat" 
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxsZWdhbCUyMG9mZmljZXxlbnwwfHx8fDE3NzU1ODE1NTR8MA&ixlib=rb-4.1.0&q=85)',
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-[#3E2723] opacity-90"></div>
        
        {/* Content */}
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Protect Your Brand with{' '}
              <span className="text-[#D7CCC8]">Professional Trademark Registration</span>
            </h1>
            <p className="text-xl text-[#D7CCC8] mb-8 max-w-3xl mx-auto">
              Secure your brand's future with expert USPTO trademark registration services. 
              Fast, reliable, and affordable trademark protection for businesses of all sizes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={() => openPopup('I would like to register my trademark')}
                size="lg"
                className="bg-[#8D6E63] hover:bg-[#6D4C41] text-white text-lg px-8 py-6"
              >
                Register My Trademark
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => openPopup()}
                size="lg"
                variant="outline"
                className="border-2 border-[#D7CCC8] text-[#D7CCC8] hover:bg-[#D7CCC8] hover:text-[#3E2723] text-lg px-8 py-6"
              >
                Free Consultation
              </Button>
            </div>

            {/* Trust Signals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
              {trustSignals.map((signal, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold text-[#8D6E63] mb-2">
                    {signal.value}
                  </div>
                  <div className="text-[#D7CCC8]">{signal.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <Card className="bg-white border-[#D7CCC8] shadow-lg">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-6">
                    About {contactInfo.businessName}
                  </h2>
                  <p className="text-[#8D6E63] text-lg mb-4 leading-relaxed">
                    We are a leading trademark registration service provider specializing in helping businesses of all sizes 
                    protect their brands through professional USPTO trademark filing services. Since our founding, we've built 
                    a reputation for excellence, integrity, and results-driven service in the complex field of trademark law.
                  </p>
                  <p className="text-[#8D6E63] text-lg mb-4 leading-relaxed">
                    With over 15 years of combined experience and more than 1,000 successful trademark registrations across 
                    diverse industries, our team of experts understands the intricacies of trademark law and the critical 
                    importance of protecting your intellectual property. We've helped startups secure their first trademark, 
                    guided established businesses through portfolio expansion, and protected entrepreneurs' innovative brand 
                    identities in an increasingly competitive marketplace.
                  </p>
                  <p className="text-[#8D6E63] text-lg mb-4 leading-relaxed">
                    What sets us apart is our commitment to personalized service and transparent communication. Unlike automated 
                    filing services, every client works with a dedicated case manager who becomes intimately familiar with your 
                    brand, business goals, and unique challenges. We take time to understand your vision, provide strategic 
                    guidance on trademark selection and protection strategies, and remain accessible throughout the entire process.
                  </p>
                  <p className="text-[#8D6E63] text-lg mb-4 leading-relaxed">
                    Our pricing structure is designed to be transparent and accessible, offering tiered packages that accommodate 
                    different needs and budgets without compromising on quality. Whether you're a solo entrepreneur launching your 
                    first product or an established corporation expanding your trademark portfolio, we provide the same level of 
                    expertise, attention to detail, and commitment to your success.
                  </p>
                  <p className="text-[#8D6E63] text-lg leading-relaxed">
                    We pride ourselves on maintaining a 98% approval rate through meticulous application preparation, comprehensive 
                    pre-filing searches, and expert handling of office actions. Our ongoing support extends well beyond registration, 
                    with renewal reminders, monitoring services, and strategic consultation to help you build and maintain a strong 
                    trademark portfolio that grows with your business. Your brand is your most valuable asset—let us help you protect it.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {whyChooseUs.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <Card key={index} className="bg-[#EFEBE9] border-[#D7CCC8] hover:shadow-md transition-shadow duration-300">
                        <CardContent className="p-6 text-center">
                          <Icon className="h-10 w-10 text-[#3E2723] mx-auto mb-3" />
                          <h3 className="font-semibold text-[#3E2723] mb-2">{item.title}</h3>
                          <p className="text-sm text-[#8D6E63]">{item.description}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trademark Overview Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#D7CCC8' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-4">
              Understanding Trademark Registration
            </h2>
            <p className="text-lg text-[#8D6E63] max-w-3xl mx-auto leading-relaxed">
              A trademark is more than just a logo or business name—it's the legal foundation of your brand identity 
              and a powerful business asset. A trademark can be a word, phrase, symbol, design, color combination, sound, 
              or any distinctive element that identifies and distinguishes your goods or services from competitors in the 
              marketplace. Federal trademark registration with the USPTO provides nationwide protection, exclusive rights, 
              legal presumptions of ownership, and the ability to enforce your rights in federal court. Understanding the 
              scope and benefits of trademark protection is the first step in safeguarding your brand's future.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-[#8D6E63]">
              <CardHeader>
                <CardTitle className="text-[#3E2723] flex items-center text-xl">
                  <Shield className="h-6 w-6 mr-2 text-[#8D6E63]" />
                  What Gets Protected
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-[#8D6E63]">
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Business names and logos:</strong> Your company name, brand identity, and visual symbols that customers associate with your business</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Product names and slogans:</strong> Distinctive product identifiers, taglines, and marketing phrases that distinguish your offerings</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Service marks and brand elements:</strong> Identifiers for services rather than products, including design elements and brand features</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Unique packaging designs:</strong> Trade dress protection for distinctive product packaging and overall commercial image</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Sounds and colors:</strong> In some cases, distinctive audio signatures or color combinations used as brand identifiers</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#8D6E63]">
              <CardHeader>
                <CardTitle className="text-[#3E2723] flex items-center text-xl">
                  <Clock className="h-6 w-6 mr-2 text-[#8D6E63]" />
                  Protection Duration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-[#8D6E63]">
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Initial registration: 10 years</strong> from the date of registration, with protection beginning from your filing date</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Renewable indefinitely:</strong> Trademarks can last forever as long as you continue using them in commerce and file renewals</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Nationwide protection:</strong> Federal registration covers all 50 states and U.S. territories, regardless of where you do business</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Continuous use required:</strong> You must maintain actual use in commerce and file required maintenance documents at specific intervals</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Maintenance filings:</strong> Section 8 Declaration (years 5-6), Section 15 Incontestability (after year 5), and renewal (year 10)</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#8D6E63]">
              <CardHeader>
                <CardTitle className="text-[#3E2723] flex items-center text-xl">
                  <Award className="h-6 w-6 mr-2 text-[#8D6E63]" />
                  Legal Rights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-[#8D6E63]">
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Exclusive use nationwide:</strong> The right to be the only business using that mark for your goods/services throughout the United States</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Legal presumption of ownership:</strong> Registration creates a legal presumption that you own the mark and have the right to use it</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Federal court jurisdiction:</strong> Ability to bring infringement lawsuits in federal court and potentially recover statutory damages and attorney fees</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Use ® symbol legally:</strong> The registered trademark symbol provides notice of your rights and deters potential infringers</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5 text-[#3E2723]" />
                    <span><strong>Customs protection:</strong> Record your mark with U.S. Customs to prevent importation of infringing or counterfeit goods</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Detailed Services Section */}
      <section id="services" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-4">
              Our Trademark Registration Services
            </h2>
            <p className="text-lg text-[#8D6E63] max-w-3xl mx-auto">
              Comprehensive trademark services designed to protect your brand at every stage
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="bg-white border-[#D7CCC8] hover:shadow-xl transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-start">
                      <div className="bg-[#3E2723] p-3 rounded-lg mr-4">
                        <Icon className="h-8 w-8 text-[#D7CCC8]" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-[#3E2723] mb-2">
                          {service.title}
                        </CardTitle>
                        <p className="text-[#8D6E63]">{service.description}</p>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => openPopup('I would like to learn more about your services')}
              size="lg"
              className="bg-[#3E2723] hover:bg-[#5D4037] text-white"
            >
              Protect My Brand Now
              <Shield className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#3E2723' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Choose {contactInfo.businessName}?
            </h2>
            <p className="text-lg text-[#D7CCC8] max-w-3xl mx-auto leading-relaxed">
              Industry-leading expertise, personalized service, and proven results set us apart in the trademark registration 
              industry. We combine deep knowledge of trademark law with practical business understanding to provide strategic 
              guidance tailored to your specific needs. Our commitment to transparency, accessibility, and client success has 
              earned us the trust of over 1,000 businesses nationwide. When you choose us, you're not just getting a filing 
              service—you're gaining a dedicated partner invested in protecting your brand's future.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="bg-[#8D6E63] border-none text-center hover:scale-105 transition-transform duration-300">
                  <CardContent className="p-6">
                    <Icon className="h-12 w-12 text-white mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-[#D7CCC8]">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-4">
              Our Trademark Registration Process
            </h2>
            <p className="text-lg text-[#8D6E63] max-w-3xl mx-auto leading-relaxed">
              A straightforward, proven three-step approach to securing your federal trademark registration. We've refined 
              our process over thousands of successful applications to provide maximum efficiency while maintaining the 
              highest standards of quality and attention to detail. Each step is designed to move your application forward 
              systematically while keeping you informed and involved in all important decisions affecting your brand protection.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <Card key={index} className="bg-white border-[#D7CCC8] hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="text-6xl font-bold text-[#D7CCC8] mb-6">{step.step}</div>
                  <h3 className="text-2xl font-semibold text-[#3E2723] mb-4">{step.title}</h3>
                  <p className="text-[#8D6E63] leading-relaxed">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4" style={{ backgroundColor: '#D7CCC8' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-4">
              Transparent Pricing Plans
            </h2>
            <p className="text-lg text-[#8D6E63] max-w-3xl mx-auto">
              Choose the perfect plan for your trademark registration needs
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, index) => (
              <PricingCard
                key={index}
                plan={plan}
                onSelectPlan={(plan) => openPopup(`I'm interested in the ${plan.name} plan`)}
                isRecommended={plan.name === 'Deluxe'}
              />
            ))}
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden">
            <div className="relative">
              <PricingCard
                plan={pricingPlans[currentPriceIndex]}
                onSelectPlan={(plan) => openPopup(`I'm interested in the ${plan.name} plan`)}
                isRecommended={pricingPlans[currentPriceIndex].name === 'Deluxe'}
              />
              
              {/* Navigation Arrows */}
              <div className="flex justify-center items-center gap-4 mt-6">
                <Button
                  onClick={prevPrice}
                  variant="outline"
                  size="icon"
                  className="border-[#8D6E63] text-[#3E2723]"
                >
                  <ChevronRight className="h-5 w-5 rotate-180" />
                </Button>
                
                {/* Dots */}
                <div className="flex gap-2">
                  {pricingPlans.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPriceIndex(index)}
                      className={`h-2 w-2 rounded-full transition-all duration-300 ${
                        index === currentPriceIndex ? 'bg-[#3E2723] w-6' : 'bg-[#8D6E63]'
                      }`}
                    />
                  ))}
                </div>
                
                <Button
                  onClick={nextPrice}
                  variant="outline"
                  size="icon"
                  className="border-[#8D6E63] text-[#3E2723]"
                >
                  <ChevronRight className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-[#8D6E63] mb-4">
              * Government filing fees are separate and paid directly to the USPTO
            </p>
            <Button
              onClick={() => openPopup()}
              size="lg"
              className="bg-[#3E2723] hover:bg-[#5D4037] text-white"
            >
              Get Started Today
            </Button>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-[#8D6E63] max-w-3xl mx-auto leading-relaxed">
              Our trademark registration expertise spans all business sectors and industries. Whether you're a tech startup 
              launching innovative software, a restaurant protecting your unique brand, a fashion designer securing your 
              clothing line, or a healthcare provider establishing your service mark, we have the experience and knowledge 
              to navigate the specific trademark challenges of your industry. Every business deserves strong trademark 
              protection, and we're here to provide it regardless of your field.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <Card key={index} className="bg-white border-[#D7CCC8] hover:border-[#8D6E63] transition-colors duration-300">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="h-8 w-8 text-[#3E2723] mx-auto mb-3" />
                  <p className="text-[#3E2723] font-medium">{industry}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#EFEBE9' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-4">
              Benefits of Trademark Registration
            </h2>
            <p className="text-lg text-[#8D6E63] max-w-3xl mx-auto leading-relaxed">
              Federal trademark registration provides powerful legal protections and business advantages that common law 
              rights cannot match. Beyond legal exclusivity, trademarks serve as valuable business assets that increase 
              company valuation, facilitate licensing and franchising opportunities, enable e-commerce platform verification, 
              and provide the foundation for building lasting brand equity. The benefits extend far beyond the registration 
              certificate—they impact every aspect of your business growth and market positioning.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="bg-white border-[#D7CCC8] hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6 flex items-start">
                    <div className="bg-[#3E2723] p-3 rounded-lg mr-4">
                      <Icon className="h-8 w-8 text-[#D7CCC8]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-[#3E2723] mb-2">{benefit.title}</h3>
                      <p className="text-[#8D6E63]">{benefit.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Signals / Achievements Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#8D6E63' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Trusted by Businesses Nationwide
            </h2>
            <p className="text-lg text-[#D7CCC8]">
              Our commitment to excellence has earned us recognition as USPTO filing experts
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="bg-white border-none text-center">
              <CardContent className="p-8">
                <Award className="h-16 w-16 text-[#3E2723] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#3E2723] mb-2">USPTO Filing Experts</h3>
                <p className="text-[#8D6E63]">Certified professionals with extensive trademark law experience</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none text-center">
              <CardContent className="p-8">
                <Star className="h-16 w-16 text-[#3E2723] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#3E2723] mb-2">4.9/5 Rating</h3>
                <p className="text-[#8D6E63]">Based on 500+ client reviews and testimonials</p>
              </CardContent>
            </Card>

            <Card className="bg-white border-none text-center">
              <CardContent className="p-8">
                <Shield className="h-16 w-16 text-[#3E2723] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#3E2723] mb-2">Money-Back Guarantee</h3>
                <p className="text-[#8D6E63]">We stand behind our work with a satisfaction guarantee</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button
              onClick={() => openPopup()}
              size="lg"
              className="bg-[#3E2723] hover:bg-[#5D4037] text-white"
            >
              Join Our Success Stories
            </Button>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="faqs" className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-[#8D6E63]">
              Get answers to common questions about trademark registration
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white border border-[#D7CCC8] rounded-lg px-6"
              >
                <AccordionTrigger className="text-left text-[#3E2723] font-semibold hover:text-[#8D6E63]">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#8D6E63]">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-12">
            <p className="text-[#8D6E63] mb-4">Still have questions?</p>
            <Button
              onClick={() => openPopup('I have a question about trademark registration')}
              className="bg-[#3E2723] hover:bg-[#5D4037] text-white"
            >
              Contact Our Experts
            </Button>
          </div>
        </div>
      </section>

      {/* Strong CTA Section */}
      <section className="py-20 px-4" style={{ backgroundColor: '#3E2723' }}>
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Ready to Protect Your Brand?
          </h2>
          <p className="text-xl text-[#D7CCC8] mb-8 max-w-2xl mx-auto">
            Don't wait to secure your trademark. Every day without protection puts your brand at risk. 
            Get started today with our expert team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={() => openPopup('I want to register my trademark today')}
              size="lg"
              className="bg-[#8D6E63] hover:bg-[#6D4C41] text-white text-lg px-8 py-6"
            >
              Register Your Trademark Now
              <Shield className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => openPopup()}
              size="lg"
              variant="outline"
              className="border-2 border-[#D7CCC8] text-[#D7CCC8] hover:bg-[#D7CCC8] hover:text-[#3E2723] text-lg px-8 py-6"
            >
              Schedule Free Consultation
            </Button>
          </div>
          <p className="text-[#D7CCC8] mt-6 text-sm">
            ⚡ Limited Time Offer: Save 20% on all packages this month
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#3E2723] mb-4">
              Get In Touch
            </h2>
            <p className="text-lg text-[#8D6E63]">
              Our trademark experts are here to help you protect your brand
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-[#D7CCC8] hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-[#3E2723] p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Mail className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#3E2723] mb-2">Email Us</h3>
                <a 
                  href={`mailto:${contactInfo.email}`}
                  className="text-[#8D6E63] hover:text-[#3E2723] transition-colors duration-300"
                >
                  {contactInfo.email}
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D7CCC8] hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-[#3E2723] p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Phone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#3E2723] mb-2">Call Us</h3>
                <a 
                  href={`tel:${contactInfo.phone}`}
                  className="text-[#8D6E63] hover:text-[#3E2723] transition-colors duration-300"
                >
                  {contactInfo.phone}
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D7CCC8] hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-6 text-center">
                <div className="bg-[#3E2723] p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <MapPin className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#3E2723] mb-2">Visit Us</h3>
                <p className="text-[#8D6E63]">{contactInfo.address}</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => openPopup()}
              size="lg"
              className="bg-[#3E2723] hover:bg-[#5D4037] text-white"
            >
              Send Us a Message
              <Mail className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4" style={{ backgroundColor: '#3E2723' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center mb-4">
                <img 
                  src="/logo-white.png" 
                  alt="TradeMark Registry Logo" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-[#D7CCC8] text-sm">
                Professional trademark registration services protecting brands nationwide.
              </p>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navigationLinks.map((link) => (
                  <li key={link.name}>
                    <a 
                      href={link.href}
                      className="text-[#D7CCC8] hover:text-white transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Services</h3>
              <ul className="space-y-2">
                <li><a href="#services" className="text-[#D7CCC8] hover:text-white transition-colors duration-300 text-sm">Trademark Filing</a></li>
                <li><a href="#services" className="text-[#D7CCC8] hover:text-white transition-colors duration-300 text-sm">Trademark Search</a></li>
                <li><a href="#services" className="text-[#D7CCC8] hover:text-white transition-colors duration-300 text-sm">Brand Protection</a></li>
                <li><a href="#services" className="text-[#D7CCC8] hover:text-white transition-colors duration-300 text-sm">Office Actions</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm text-[#D7CCC8]">
                <li>{contactInfo.phone}</li>
                <li>{contactInfo.email}</li>
                <li>{contactInfo.address}</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#8D6E63] pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-[#D7CCC8] text-sm mb-4 md:mb-0">
                © {new Date().getFullYear()} {contactInfo.businessName}. All rights reserved.
              </p>
              <div className="flex gap-4">
                <a href="/privacy" className="text-[#D7CCC8] hover:text-white transition-colors duration-300 text-sm">
                  Privacy Policy
                </a>
                <span className="text-[#D7CCC8]">|</span>
                <a href="/terms" className="text-[#D7CCC8] hover:text-white transition-colors duration-300 text-sm">
                  Terms & Conditions
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Popup */}
      <ContactPopup 
        isOpen={isPopupOpen} 
        onClose={() => setIsPopupOpen(false)}
        defaultMessage={popupMessage}
      />
    </div>
  );
};

export default Home;
